import { SelectProps, SlotProps } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import React from 'react';

import { ItemWithCheckbox } from '@/components/molecules/SelectItemWithCheckbox';
import { useResolvedItems } from '@/hooks/useResolvedItems';
import { useTranslator } from '@/hooks/useTranslator';
import { ItemsResolver, SelectItem } from '@/types/fields';

interface Props<ItemValueType extends string | number> {
  readonly name?: string;
  readonly label?: string;
  readonly source: ItemsResolver<ItemValueType> | readonly SelectItem<ItemValueType>[];
  readonly defaultValue?: readonly ItemValueType[] | '';
}

export function MultipleSelectionCustomSelect<ItemValueType extends string | number>(
  props: Props<ItemValueType>,
): React.ReactElement {
  const tr = useTranslator();

  const { name, label, source, defaultValue = [] } = props;
  const [selection, setSelection] = React.useState<readonly ItemValueType[]>(
    defaultValue === '' ? [] : defaultValue,
  );

  const items = useResolvedItems(source);

  const onChangeWrapper = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;

    // Simply ensure everything runs before sending the event (like closing the popup)
    if (typeof value === 'string') {
      throw new Error(
        'In multiple selection, value should be an array or the empty string, not a string',
      );
    }
    setSelection(value);
  }, []);

  const labelsMap = React.useMemo((): Record<string | number, string> => {
    const emptyValue: Record<string | number, string> = {};
    const reducer = (
      mapped: Record<string | number, string>,
      item: SelectItem<ItemValueType>,
    ): Record<string | number, string> => {
      return {
        ...mapped,
        [item.value]: tr(item.label),
      };
    };

    return items.reduce(reducer, emptyValue);
  }, [items, tr]);

  const checkedItems = React.useMemo((): { readonly [key in ItemValueType]?: boolean } => {
    if (!Array.isArray(defaultValue)) {
      return {};
    }
    const reducer = (
      current: { readonly [key in ItemValueType]?: boolean },
      item: ItemValueType,
    ): { readonly [key in ItemValueType]?: boolean } => ({
      ...current,
      [item]: true,
    });

    return defaultValue.reduce(reducer, {});
  }, [defaultValue]);

  const renderItem = React.useCallback(
    (item: SelectItem<ItemValueType>): React.ReactNode => {
      // noinspection PointlessBooleanExpressionJS (not pointless, I promise)
      const checked: boolean = checkedItems[item.value] === true;
      return <ItemWithCheckbox item={item} checked={checked} />;
    },
    [checkedItems],
  );

  const value = React.useMemo((): string => JSON.stringify(selection), [selection]);
  const slotProps = React.useMemo((): SlotProps<any, any, any> => {
    const selectProps = createSelectProps(labelsMap);

    return {
      select: selectProps,
      input: {
        'data-type': 'array',
      },
    };
  }, [labelsMap]);

  return (
    <Box position="relative">
      <input name={name} type="hidden" value={value} />

      <TextField
        variant="outlined"
        select={true}
        value={selection}
        label={label}
        fullWidth={true}
        slotProps={slotProps}
        onChange={onChangeWrapper}>
        {items.map(
          (item: SelectItem<ItemValueType>): React.ReactElement => (
            <MenuItem key={item.value} value={item.value}>
              {renderItem(item)}
            </MenuItem>
          ),
        )}
      </TextField>
    </Box>
  );
}

function createSelectProps<T extends string | number = string>(labels: {
  [key: string | number]: string;
}): SelectProps {
  return {
    displayEmpty: true,
    multiple: true,
    renderValue: (value: unknown): React.ReactNode => {
      if (!Array.isArray(value)) {
        return '';
      }

      return value.map((value: T): string => labels[value]).join(', ');
    },
  };
}
